/**
 * ComposedActionButton.js — Condition/Cost-Aware Action Button
 *
 * Wraps DomainCardButton with automatic condition evaluation and cost
 * validation. Determines disabled/active state from the engine and
 * delegates execution to the parent renderer.
 *
 * Part of the Composable Mechanic Engine (Issue #82, Phase A: #176)
 */

import React, { useMemo } from 'react';
import { DomainCardButton } from '../components/domain-cards/ui';
import { evaluateConditions } from './conditions';
import { validateCosts } from './costs';

/**
 * @param {object} props
 * @param {object} props.action - Action definition from mechanic.actions[]
 * @param {object} props.character - Character object
 * @param {string} props.cardKey - Card key for resource lookups
 * @param {object} [props.sessionState={}] - Session state for condition checks
 * @param {object} [props.cooldowns={}] - Active cooldowns
 * @param {boolean} [props.disabled=false] - External disable (e.g., Beastform)
 * @param {string} [props.disabledReason=''] - External disable reason
 * @param {function} props.onExecute - Callback: (action) => void
 */
const ComposedActionButton = ({
  action,
  character,
  cardKey,
  sessionState = {},
  cooldowns = {},
  disabled: externalDisabled = false,
  disabledReason: externalDisabledReason = '',
  onExecute,
}) => {
  const context = { character, cardKey, sessionState, cooldowns };

  // Evaluate conditions and costs
  const conditionResult = useMemo(
    () => evaluateConditions(action.conditions || [], context),
    [action.conditions, character, cardKey, sessionState, cooldowns]
  );

  const costResult = useMemo(
    () => validateCosts(action.costs || [], context),
    [action.costs, character, cardKey]
  );

  const canActivateInternal = conditionResult.canActivate && costResult.canPay;
  const canActivate = externalDisabled ? false : canActivateInternal;

  // Determine disable reason
  const internalReason = !conditionResult.canActivate
    ? conditionResult.reason
    : !costResult.canPay
      ? costResult.reason
      : null;
  const disabledReason = externalDisabled ? externalDisabledReason : internalReason;

  // Auto-detect active state from set_session_state or set_character_flag effects
  const isActive = useMemo(() => {
    const stateEffect = (action.effects || []).find(e => e.type === 'set_session_state');
    if (stateEffect && !!sessionState[stateEffect.key]) return true;
    const flagEffect = (action.effects || []).find(e => e.type === 'set_character_flag');
    if (flagEffect && !!character[flagEffect.flag]) return true;
    return false;
  }, [action.effects, sessionState, character]);

  // Determine button label
  const getLabel = () => {
    if (isActive && action.toggleOff?.label) return action.toggleOff.label;
    if (isActive && action.labels?.active) return action.labels.active;
    if (!canActivate && action.labels?.disabled) {
      const disabledLabels = action.labels.disabled;
      if (typeof disabledLabels === 'string') return disabledLabels;
      if (typeof disabledLabels === 'object') {
        if (disabledReason?.includes('Stress') && disabledLabels.noStress) return disabledLabels.noStress;
        if (disabledReason?.includes('Hope') && disabledLabels.noHope) return disabledLabels.noHope;
        if (disabledReason?.includes('active') && disabledLabels.active) return disabledLabels.active;
        if (disabledLabels.default) return disabledLabels.default;
      }
    }
    return action.label;
  };

  // Determine variant
  const getVariant = () => {
    if (isActive && action.toggleOff) return action.toggleOff.variant || 'activeSuccess';
    if (isActive) return action.activeVariant || 'active';
    if (!canActivate) return 'disabled';
    return action.variant || 'primary';
  };

  return (
    <DomainCardButton
      onClick={() => {
        if (isActive && action.toggleOff) {
          onExecute(action, { isToggleOff: true });
        } else {
          onExecute(action);
        }
      }}
      disabled={!canActivate && !isActive}
      isActive={isActive}
      variant={getVariant()}
      title={disabledReason || undefined}
    >
      {action.icon && <span className="mr-1">{action.icon}</span>}
      {getLabel()}
    </DomainCardButton>
  );
};

export default ComposedActionButton;
